import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption } from '@nx-smartmonkey/ui';

import { getVehicleColor } from '../../../constants/Colors';
import { VehicleFlat } from '../../../domain/vehicle/VehicleFlat';
import { getRootLocation, rootNavigate } from '../../../helpers/CustomRouter';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useAnalyticsFilters } from '../../../hooks/analytics/useAnalyticsFilters';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRouterQuery } from '../../../hooks/useRouterQuery';
import { useRetrieveFlatVehicles } from '../../../hooks/vehicle/useRetrieveFlatVehicles';
import Text from '../../../react-components/Text';

interface AnalyticsVehiclesFilterProps {
  isSingleVehicle?: boolean;
}

const AnalyticsVehiclesFilter = ({ isSingleVehicle }: AnalyticsVehiclesFilterProps) => {
  const urlParams = useRouterQuery();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectVehicles } = useRetrieveFlatVehicles({ project: activeProject });
  const { vehicles } = useAnalyticsFilters({ project: activeProject });

  const setVehicles = ({ newVehicles }: { newVehicles: Array<VehicleFlat> }) => {
    if ((newVehicles ?? []).length === 0) {
      urlParams.delete(`vehicle_ids`);
    } else {
      if (isSingleVehicle) {
        urlParams.set(`vehicle_ids`, newVehicles[0].id);
      } else {
        urlParams.delete(`vehicle_ids`);
        (newVehicles ?? []).forEach((vehicle) => {
          urlParams.append(`vehicle_ids`, vehicle.id);
        });
      }
    }
    rootNavigate(`${getRootLocation().pathname}?${urlParams.toString()}`);
  };

  if (!projectVehicles) {
    return null;
  }

  return (
    <Grid
      item
      container
      direction="row"
      alignItems="center"
      wrap="nowrap"
      style={{
        padding: `16px 21px`,
        borderBottom: `1px solid ${RoutalPalette.grey4}`,
      }}
    >
      {!isSingleVehicle ? (
        <>
          <Grid item>
            <RoutalSelect
              id="vehicles-select-multiple"
              containerStyle={{
                width: `200px`,
              }}
              popoverProps={{
                options: projectVehicles.map(
                  (vehicle: VehicleFlat): RoutalSelectMultiSelectOption => ({
                    key: `vehicle-${vehicle.id}`,
                    hideCheckIcon: false,
                    closeOnClick: false,
                    checked: vehicles.map((v) => v.id).includes(vehicle.id),
                    // disabled: isSingleVehicle ? (vehicles.map(v => v.id)).includes(vehicle.id) : false,
                    onClick: () => {
                      if (vehicles.map((v) => v.id).includes(vehicle.id)) {
                        setVehicles({
                          newVehicles: [...vehicles.filter((v) => v.id !== vehicle.id)],
                        });
                      } else {
                        setVehicles({
                          newVehicles: [...vehicles, vehicle],
                        });
                      }
                    },
                    content: (
                      <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                        <Grid item>
                          <div
                            style={{
                              width: `12px`,
                              height: `12px`,
                              borderRadius: `50%`,
                              backgroundColor: getVehicleColor(vehicle, projectVehicles),
                            }}
                          />
                        </Grid>
                        <HSpacer small />
                        <Text
                          tagName="span"
                          style={{
                            textAlign: `justify`,
                            whiteSpace: `nowrap`,
                            overflow: `hidden`,
                            width: `100%`,
                            textOverflow: `ellipsis`,
                            cursor: `pointer`,
                          }}
                        >
                          {vehicle.label ? vehicle.label : intl.formatMessage({ id: `no_name` })}
                        </Text>
                      </Grid>
                    ),
                  })
                ),
              }}
            >
              <Text
                style={{
                  cursor: `pointer`,
                  width: `100%`,
                  textAlign: `justify`,
                }}
                intlId="vehicles_filter.add_vehicle"
              />
            </RoutalSelect>
          </Grid>

          <HSpacer medium />

          <Grid container direction="row" alignItems="center" style={{ gap: `10px` }}>
            {vehicles.map((vehicle: VehicleFlat, index: number) => {
              return (
                <Grid
                  item
                  container
                  direction="row"
                  wrap="nowrap"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    width: `unset`,
                    minWidth: `140px`,
                    borderRadius: `2px`,
                    backgroundColor: `#E0E0E0`,
                    padding: `8px`,
                  }}
                >
                  <Grid item>
                    <Grid container direction="row" wrap="nowrap" alignItems="center">
                      <div
                        style={{
                          width: `12px`,
                          height: `12px`,
                          borderRadius: `50%`,
                          backgroundColor: getVehicleColor(vehicle, projectVehicles),
                        }}
                      />
                      <HSpacer small />
                      <Text>{vehicle.label ? vehicle.label : intl.formatMessage({ id: `no_name` })}</Text>
                    </Grid>
                  </Grid>

                  <span
                    style={{
                      width: `14px`,
                      height: `14px`,
                      borderRadius: `50%`,
                      backgroundColor: `#393939`,
                      cursor: `pointer`,
                      lineHeight: 1,
                      color: `white`,
                      fontSize: `12px`,
                      textAlign: `center`,
                      marginLeft: `8px`,
                    }}
                    onClick={() => setVehicles({ newVehicles: vehicles.filter((v) => v.id !== vehicle.id) })}
                  >
                    x
                  </span>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : (
        <RoutalSelect
          id="vehicles-select-single"
          containerStyle={{
            width: `200px`,
          }}
          popoverProps={{
            options: [
              {
                key: `not-assigned`,
                onClick: () => {
                  setVehicles({ newVehicles: [] });
                },
                content: (
                  <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                    <Text
                      tagName="span"
                      style={{
                        textAlign: `justify`,
                        whiteSpace: `nowrap`,
                        overflow: `hidden`,
                        width: `100%`,
                        textOverflow: `ellipsis`,
                        cursor: `pointer`,
                      }}
                      intlId="none"
                    />
                  </Grid>
                ),
              },
              ...projectVehicles.map(
                (vehicle: VehicleFlat): RoutalSelectMultiSelectOption => ({
                  key: `vehicle-${vehicle.id}`,
                  hideCheckIcon: false,
                  closeOnClick: true,
                  checked: vehicles.map((v) => v.id).includes(vehicle.id),
                  // disabled: isSingleVehicle ? (vehicles.map(v => v.id)).includes(vehicle.id) : false,
                  onClick: () => {
                    setVehicles({
                      newVehicles: [vehicle],
                    });
                  },
                  content: (
                    <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                      <Grid item>
                        <div
                          style={{
                            width: `12px`,
                            height: `12px`,
                            borderRadius: `50%`,
                            backgroundColor: getVehicleColor(vehicle, projectVehicles),
                          }}
                        />
                      </Grid>
                      <HSpacer small />
                      <Text
                        tagName="span"
                        style={{
                          textAlign: `justify`,
                          whiteSpace: `nowrap`,
                          overflow: `hidden`,
                          width: `100%`,
                          textOverflow: `ellipsis`,
                          cursor: `pointer`,
                        }}
                      >
                        {vehicle.label ? vehicle.label : intl.formatMessage({ id: `no_name` })}
                      </Text>
                    </Grid>
                  ),
                })
              ),
            ],
          }}
        >
          <>
            {vehicles.length === 0 ? (
              <Text
                style={{
                  cursor: `pointer`,
                  width: `100%`,
                  textAlign: `justify`,
                }}
                intlId="vehicles_filter.select_vehicle"
              />
            ) : (
              <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                <Grid item>
                  <div
                    style={{
                      width: `12px`,
                      height: `12px`,
                      borderRadius: `50%`,
                      backgroundColor: getVehicleColor(vehicles[0], projectVehicles),
                    }}
                  />
                </Grid>
                <HSpacer small />
                <Text
                  tagName="span"
                  style={{
                    textAlign: `justify`,
                    whiteSpace: `nowrap`,
                    overflow: `hidden`,
                    width: `100%`,
                    textOverflow: `ellipsis`,
                    cursor: `pointer`,
                  }}
                >
                  {vehicles[0].label ? vehicles[0].label : intl.formatMessage({ id: `no_name` })}
                </Text>
              </Grid>
            )}
          </>
        </RoutalSelect>
      )}
    </Grid>
  );
};

export default AnalyticsVehiclesFilter;
